import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import { useTheme } from "../context/ThemeContext";

const ConfirmOfferStatusModal = ({
  open,
  onClose,
  onConfirm,
  currentStatus,
  initialRefusalReason = "",
}) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [refusalReason, setRefusalReason] = useState("");
  const { darkMode } = useTheme();

  useEffect(() => {
    setRefusalReason(initialRefusalReason || "");
    setSelectedStatus(null);
  }, [open, initialRefusalReason]);

  const handleConfirm = () => {
    const statusToSend = selectedStatus ?? currentStatus;
    onConfirm(statusToSend, refusalReason);
    onClose();
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Prospect";
      case 1:
        return "Finalizat";
      case 2:
        return "Refuzat";
      default:
        return "Necunoscut";
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 1:
        return "#66bb6a"; // green
      case 2:
        return "#f44336"; // red
      case 0:
      default:
        return "#42a5f5"; // blue
    }
  };

  const availableStatuses = [0, 1, 2].filter(
    (status) => status !== currentStatus
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
        Schimbare status ofertă
      </DialogTitle>

      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={2}
        >
          <SyncAltRoundedIcon sx={{ fontSize: 50, color: "#42a5f5", mb: 2 }} />

          <Typography variant="body1" fontSize="18px">
            Statut actual:
          </Typography>

          <Typography
            variant="h6"
            fontWeight="bold"
            mt={0.5}
            mb={2}
            sx={{ color: statusColor(currentStatus) }}
          >
            {getStatusLabel(currentStatus)}
          </Typography>
          {currentStatus === 2 && initialRefusalReason && (
            <Box
              mt={2}
              width="100%"
              textAlign="left"
              sx={{
                backgroundColor: darkMode ? "#1e1e1e" : "#f3f3f3",
                border: "1px solid",
                borderColor: darkMode ? "#444" : "#ccc",
                borderRadius: "8px",
                padding: 2,
              }}
              className="bg-gray-100 dark:bg-gray-800 border rounded-lg p-3"
            >
              <Typography
                variant="body2"
                fontWeight="bold"
                gutterBottom
                className={darkMode ? "text-white" : "text-black"}
              >
                Motivul refuzului existent:
              </Typography>
              <Typography
                variant="body2"
                className={darkMode ? "text-gray-300" : "text-gray-800"}
              >
                {initialRefusalReason}
              </Typography>
            </Box>
          )}

          <Typography variant="body1" fontSize="18px" mb={1} mt={4}>
            Alege noul statut:
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
          pb: 2,
          px: 3,
          flexDirection: "column",
        }}
      >
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2}>
          {availableStatuses.map((status) => (
            <Button
              key={status}
              variant={selectedStatus === status ? "contained" : "outlined"}
              onClick={() => {
                setSelectedStatus(status);
                if (status !== 2) {
                  onConfirm(status, ""); // Automatikus módosítás Refuzat nélkül
                  onClose(); // Bezárjuk a modált is
                }
              }}
              sx={{
                borderRadius: "999px",
                px: 4,
                py: 1.5,
                backgroundColor:
                  selectedStatus === status ? statusColor(status) : undefined,
                color: selectedStatus === status ? "white" : undefined,
                "&:hover": {
                  backgroundColor: `${statusColor(status)}cc`,
                },
              }}
            >
              {getStatusLabel(status)}
            </Button>
          ))}
        </Box>

        {(currentStatus === 2 || selectedStatus === 2) && (
          <>
            <Box mt={2} width="100%">
              <Typography variant="body1" fontWeight="bold" mb={1}>
                Motivul refuzului:
              </Typography>
              <textarea
                value={refusalReason}
                onChange={(e) => setRefusalReason(e.target.value)}
                rows={6}
                placeholder="ex: Preț prea mare, clientul a decis altceva..."
                className={`w-full h-40 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkMode
                    ? "bg-gray-800 text-white border-gray-600 placeholder-gray-400"
                    : "bg-white text-black border-gray-300 placeholder-gray-500"
                }`}
              />
            </Box>

            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Button
                onClick={handleConfirm}
                variant="contained"
                sx={{
                  borderRadius: "999px",
                  px: 4,
                  py: 1.5,
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#115293",
                  },
                }}
              >
                Confirmă
              </Button>

              <Button
                onClick={onClose}
                color="inherit"
                variant="outlined"
                sx={{ borderRadius: "999px", px: 4, py: 1.5 }}
              >
                Anulează
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmOfferStatusModal;
